import { useMemo, useState } from "react";

import { PageHeader } from "../../components/ui/PageHeader";
import { formatAbsolute } from "../../lib/datetime";
import { useTelemetry } from "../../hooks/useTelemetry";
import { useIsMobile } from "../../lib/useMediaQuery";
import type { LatencyStat, TopEngagement, VolumeBucket } from "../../api/telemetry";

type Window = 1 | 24 | 168;

const WINDOW_LABEL: Record<Window, string> = {
  1: "1h",
  24: "24h",
  168: "7d",
};

function shortUuid(u: string): string {
  return u.split("-")[0];
}

function fmtPct(x: number): string {
  return `${(x * 100).toFixed(1)}%`;
}

function fmtTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export function TelemetryOverview() {
  const [windowSel, setWindowSel] = useState<Window>(24);
  const isMobile = useIsMobile();
  const { data, isLoading, isError } = useTelemetry(windowSel);

  const llmStat = useMemo<LatencyStat | undefined>(
    () => data?.latencies.find((s) => s.stage === "llm"),
    [data],
  );
  const recOk = useMemo(() => {
    const r = data?.recording;
    if (!r) return null;
    const denom = r.started || 0;
    return denom > 0 ? r.stopped / denom : null;
  }, [data]);

  if (isLoading) return <StatusBlock label="Loading telemetry…" />;
  if (isError || !data) return <StatusBlock label="Failed to load telemetry." tone="error" />;

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <PageHeader
        eyebrow="Operator console"
        title="System health"
        subtitle="Operational signals and cost rollup across all calls. Updates every poll cycle."
        meta={
          <span>
            <span style={{ color: "var(--text-secondary)" }}>{data.calls_count}</span> calls
            <Dot />
            <span style={{ color: "var(--text-secondary)" }}>{data.turns_count}</span> turns
            <Dot />
            <span style={{ color: "var(--text-muted)" }}>last {WINDOW_LABEL[windowSel]}</span>
          </span>
        }
        actions={<WindowPicker value={windowSel} onChange={setWindowSel} />}
      />

      <SectionLabel>HEALTH</SectionLabel>

      <KpiStrip>
        <Kpi
          label="ERR RATE"
          value={fmtPct(data.error_rate)}
          tone={data.error_rate > 0.05 ? "error" : "ok"}
        />
        <Kpi
          label="LLM P50"
          value={llmStat ? String(llmStat.p50_ms) : "—"}
          unit={llmStat ? "ms" : undefined}
          tone="accent"
        />
        <Kpi
          label="REC OK"
          value={recOk == null ? "—" : fmtPct(recOk)}
          tone={recOk == null ? "muted" : recOk >= 0.99 ? "ok" : "warn"}
        />
        <Kpi
          label="REC FAILED"
          value={String(data.recording.failed)}
          tone={data.recording.failed > 0 ? "error" : "muted"}
        />
        <Kpi
          label="REC ACCESSED"
          value={String(data.recording.accessed)}
          tone="accent"
        />
      </KpiStrip>

      <Panel title="LATENCY · P50 / P95 (MS)">
        {data.latencies.length === 0 ? (
          <Empty>No latency samples in this window.</Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {data.latencies.map((s) => (
              <LatencyRow key={s.stage} stat={s} stacked={isMobile} />
            ))}
          </div>
        )}
      </Panel>

      <SectionLabel>COST &amp; USAGE</SectionLabel>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 14,
        }}
      >
        <Panel title={`TOKENS · ${fmtTokens(data.tokens_total)} TOTAL`}>
          {data.tokens_by_model.length === 0 ? (
            <Empty>No token usage recorded.</Empty>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {data.tokens_by_model.map((m) => (
                <Row key={m.model} left={m.model} right={fmtTokens(m.tokens)} />
              ))}
            </div>
          )}
        </Panel>

        <Panel title="TOP ENGAGEMENTS · TURNS">
          {data.top_engagements.length === 0 ? (
            <Empty>No engagement activity yet.</Empty>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {data.top_engagements.map((e) => (
                <EngagementRow key={e.engagement_id} item={e} />
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title={`TURN VOLUME · LAST ${WINDOW_LABEL[windowSel].toUpperCase()}`}>
        <VolumeChart buckets={data.volume} />
      </Panel>
    </div>
  );
}

/* ============================================================ atoms */

function WindowPicker({ value, onChange }: { value: Window; onChange: (v: Window) => void }) {
  const options: Window[] = [1, 24, 168];
  return (
    <div
      style={{
        display: "inline-flex",
        background: "var(--bg-secondary)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-input)",
        overflow: "hidden",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
      }}
    >
      {options.map((opt, i) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: "7px 14px",
              border: "none",
              borderLeft: i === 0 ? "none" : "1px solid var(--border-subtle)",
              background: active ? "var(--accent-subtle)" : "transparent",
              color: active ? "var(--accent)" : "var(--text-muted)",
              fontFamily: "inherit",
              fontSize: "inherit",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              fontWeight: active ? 600 : 500,
            }}
          >
            {WINDOW_LABEL[opt]}
          </button>
        );
      })}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.20em",
        color: "var(--accent)",
        margin: "26px 0 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ flex: "0 0 auto" }}>◆</span>
      <span>{children}</span>
      <span
        aria-hidden
        style={{
          flex: 1,
          height: 1,
          background: "linear-gradient(90deg, var(--accent-line), transparent)",
        }}
      />
    </div>
  );
}

function KpiStrip({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function Kpi({
  label,
  value,
  unit,
  tone = "muted",
}: {
  label: string;
  value: string;
  unit?: string;
  tone?: "ok" | "warn" | "error" | "accent" | "muted";
}) {
  const colorMap: Record<NonNullable<Parameters<typeof Kpi>[0]["tone"]>, string> = {
    ok: "var(--success)",
    warn: "var(--warning)",
    error: "var(--error)",
    accent: "var(--accent)",
    muted: "var(--text-primary)",
  };
  const color = colorMap[tone];
  return (
    <div
      style={{
        padding: "18px 20px",
        borderRight: "1px solid var(--border-subtle)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        position: "relative",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: 2,
          background: color,
          opacity: 0.6,
        }}
      />
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.18em",
          color: "var(--text-muted)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(20px, 6vw, 28px)",
          fontWeight: 500,
          color,
          lineHeight: 1,
          letterSpacing: "-0.01em",
          display: "inline-flex",
          alignItems: "baseline",
          gap: 4,
        }}
      >
        {value}
        {unit ? (
          <span
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontWeight: 400,
              letterSpacing: "0.04em",
            }}
          >
            {unit}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        marginTop: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "11px 18px",
          borderBottom: "1px solid var(--border-subtle)",
          background: "var(--bg-secondary)",
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.18em",
          color: "var(--text-muted)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        
        {title}
      </div>
      <div style={{ padding: "14px 18px" }}>{children}</div>
    </div>
  );
}

function LatencyRow({ stat, stacked = false }: { stat: LatencyStat; stacked?: boolean }) {
  const max = Math.max(stat.p95_ms, 1);
  const p50Pct = Math.min(100, (stat.p50_ms / max) * 100);

  const stageLabel = (
    <span
      style={{
        color: "var(--text-secondary)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.04em",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={stat.stage}
    >
      {stat.stage}
    </span>
  );

  const bar = (
    <div
      style={{
        height: 4,
        background: "var(--bg-tertiary)",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* p95 ghost bar */}
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "var(--accent)",
          opacity: 0.18,
        }}
      />
      {/* p50 bar */}
      <div
        style={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: `${p50Pct}%`,
          background: "linear-gradient(90deg, var(--accent), var(--accent-bright))",
          boxShadow: "0 0 8px rgba(94,234,212,0.35)",
        }}
      />
    </div>
  );

  const p50p95 = (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        color: "var(--text-primary)",
        textAlign: "right",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {stat.p50_ms}
      <span style={{ color: "var(--text-muted)", margin: "0 6px" }}>/</span>
      {stat.p95_ms}
    </span>
  );

  const samples = (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--text-muted)",
        textAlign: "right",
        letterSpacing: "0.04em",
      }}
      title={`${stat.samples} samples`}
    >
      n={stat.samples}
    </span>
  );

  if (stacked) {
    // Mobile: line 1 = stage label (left) + p50/p95 + n (right); line 2 = full-width bar.
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "9px 0",
          borderBottom: "1px dashed var(--border-subtle)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 10,
          }}
        >
          {stageLabel}
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 10 }}>
            {p50p95}
            {samples}
          </span>
        </div>
        {bar}
      </div>
    );
  }

  // Desktop: unchanged 4-track grid.
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(200px, 280px) 1fr 130px 70px",
        gap: 16,
        alignItems: "center",
        padding: "9px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      {stageLabel}
      {bar}
      {p50p95}
      {samples}
    </div>
  );
}

function Row({ left, right }: { left: string; right: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "8px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>{left}</span>
      <span
        style={{
          color: "var(--text-primary)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {right}
      </span>
    </div>
  );
}

function EngagementRow({ item }: { item: TopEngagement }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "8px 0",
        borderBottom: "1px dashed var(--border-subtle)",
      }}
      title={item.engagement_id}
    >
      <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>
        {item.display_name ?? (
          <span style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}>
            {shortUuid(item.engagement_id)}
          </span>
        )}
      </span>
      <span
        style={{
          color: "var(--text-primary)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {item.turns}
      </span>
    </div>
  );
}

function VolumeChart({ buckets }: { buckets: VolumeBucket[] }) {
  const max = Math.max(1, ...buckets.map((b) => b.turns));
  const [hover, setHover] = useState<number | null>(null);
  const hovered = hover != null ? buckets[hover] : null;

  return (
    <div style={{ position: "relative", paddingTop: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 2,
          height: 96,
        }}
        onMouseLeave={() => setHover(null)}
      >
        {buckets.map((b, i) => {
          const h = (b.turns / max) * 100;
          const isHovered = hover === i;
          const empty = b.turns === 0;
          return (
            <div
              key={b.hour_start}
              role="button"
              tabIndex={0}
              aria-label={`${b.turns} turns at ${formatAbsolute(b.hour_start)}`}
              onMouseEnter={() => setHover(i)}
              onClick={() => setHover((prev) => (prev === i ? null : i))}
              onTouchStart={() => setHover(i)}
              style={{
                flex: 1,
                minWidth: 3,
                height: `${Math.max(2, h)}%`,
                background: empty
                  ? isHovered
                    ? "var(--bg-hover)"
                    : "var(--bg-tertiary)"
                  : isHovered
                    ? "var(--accent-bright)"
                    : "var(--accent)",
                boxShadow: empty
                  ? "none"
                  : isHovered
                    ? "0 0 12px rgba(94,234,212,0.6)"
                    : "0 0 4px rgba(94,234,212,0.20)",
                cursor: "pointer",
                opacity: empty ? 0.7 : 1,
              }}
            />
          );
        })}
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          color: "var(--text-muted)",
          letterSpacing: "0.10em",
        }}
      >
        <span>{buckets[0] ? formatAbsolute(buckets[0].hour_start) : ""}</span>
        <span style={{ color: "var(--accent)" }}>● NOW</span>
      </div>

      <div
        aria-live="polite"
        style={{
          position: "absolute",
          top: -8,
          left:
            hover != null
              ? `${(hover / Math.max(1, buckets.length - 1)) * 100}%`
              : 0,
          transform: "translateX(-50%)",
          background: "var(--bg-tertiary)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-input)",
          padding: "6px 10px",
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          color: "var(--text-primary)",
          whiteSpace: "nowrap",
          opacity: hovered ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 0.1s",
          boxShadow: "var(--shadow-md)",
          zIndex: 2,
        }}
      >
        {hovered ? (
          <>
            <span
              style={{
                color: "var(--accent)",
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {hovered.turns}
            </span>
            <span style={{ color: "var(--text-muted)" }}> TURNS · </span>
            <span style={{ color: "var(--text-secondary)" }}>
              {formatAbsolute(hovered.hour_start)}
            </span>
          </>
        ) : (
          "—"
        )}
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.16em",
        color: "var(--text-muted)",
        textAlign: "center",
        padding: "20px 0",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function StatusBlock({ label, tone }: { label: string; tone?: "error" }) {
  return (
    <div
      style={{
        padding: "32px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        color: tone === "error" ? "var(--error)" : "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
  );
}

function Dot() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 3,
        height: 3,
        background: "var(--text-muted)",
        margin: "0 10px 2px",
        verticalAlign: "middle",
      }}
    />
  );
}
